/* The monthly summary sheet — the MIS spinning summary, line for line.
 *
 * Sl no, description, standard, the month's value, variance and the basis
 * the figure rests on, in the MIS's own groups. Standards appear only
 * where the reports carry one (SITRA kg for production, the dashboard
 * benchmark for utilisation x efficiency). A row whose source the app does
 * not have yet — mixing issue, maintenance spend, the wage bill — is shown
 * faded with what it needs, so the sheet keeps the MIS's shape and says
 * where it is still blank.
 *
 * The month's totals and the 40s-converted figures come from the server;
 * the cost lines are worked out here from the page's cost inputs
 * (lib/cost.ts), the same way the KPI tiles do it.
 */

import { Subline } from '../../App'
import { production, unitLabel } from '../../lib/api'
import type { MonthSummary, Unit } from '../../lib/api'
import { useAsync } from '../../lib/useAsync'
import { CANDY_KG, costPerKg, cottonPerKg, lintPerKg } from '../../lib/cost'
import { monthName, num } from '../../lib/format'
import { Blueprint, Empty, ErrorBox, Labelled, Loading } from '../../components/ui'

const COMPANY = 'Mohans Sri Chand Tex'
const R: React.CSSProperties = { textAlign: 'right' }

const NOTE = 'Layout follows the MIS spinning summary. Standards appear only where the '
  + 'reports carry one (standard kg for production, the dashboard benchmark for '
  + 'utilisation × efficiency); variance is shown against those. Faded rows need a '
  + 'source the dashboard does not have yet — mixing issue, maintenance and stores '
  + 'spend, wage bill, packing cost and waste sale rates. Cost lines use the candy '
  + 'rate, power tariffs and monthly overhead set in the page\'s cost inputs; sale '
  + 'value comes from the yarn sales register and is plant-wide even when one unit '
  + 'is selected.'

type SheetRow =
  | { kind: 'group'; group: string }
  | {
    kind: 'row'; sl: number; desc: string; std: string; val: string
    variance: string; variancePct: string; color: string; basis: string; faded: boolean
  }

export function SheetTab({ month, setMonth, unit, goToData }: {
  month: string | undefined
  setMonth: (m: string) => void
  unit: Unit | null
  goToData: () => void
}) {
  const { data, error, loading, reload } = useAsync(
    () => production.summary(month, unit), [month, unit])

  if (error) return <ErrorBox error={error} onRetry={reload} />
  if (loading && !data) return <Loading what="the month" />
  if (!data) return null
  if (data.empty) {
    return (
      <Empty title="No production data yet">
        Load the daily tracker workbook on the <b>Data files</b> tab and this fills in.
      </Empty>
    )
  }

  const mName = monthName(data.month, true)
  const scope = unit ? `${unitLabel(unit)} only` : 'both units'
  const rows = sheetRows(data)

  return (
    <>
      <Subline>{mName} · {data.days} operating days · {scope}</Subline>

      <div className="no-print" style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      }}>
        <Labelled label="Month">
          <select value={data.month} onChange={(e) => setMonth(e.target.value)}
            style={{ minWidth: 190 }}>
            {[...data.months].reverse().map((m) => (
              <option key={m} value={m}>{monthName(m, true)}</option>
            ))}
          </select>
        </Labelled>
        <button className="btn btn-secondary" style={{ alignSelf: 'flex-end' }} onClick={goToData}>
          Update data
        </button>
      </div>

      <Blueprint className="sheet" style={{ padding: '24px 26px 22px' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="eyebrow" style={{ color: 'var(--color-accent-700)' }}>
            {COMPANY}{unit ? ` · ${unitLabel(unit)}` : ''}
          </div>
          <h2 style={{ fontSize: 26, margin: '6px 0 0', letterSpacing: '0.02em' }}>
            Spinning summary for the month of {mName}
          </h2>
          <div style={{ fontSize: 12, color: 'var(--color-neutral-600)', marginTop: 4 }}>
            {data.days} operating days · {scope}
          </div>
        </div>
        <div className="scroll-x" style={{ marginTop: 20 }}>
          <table className="table" style={{ width: '100%', minWidth: 860 }}>
            <thead>
              <tr>
                <th style={{ ...R, width: 56 }}>Sl no</th>
                <th>Description</th>
                <th style={R}>Standard</th>
                <th style={R}>Value ({mName.slice(0, 3)})</th>
                <th style={R}>Variance</th>
                <th style={R}>Variance %</th>
                <th>Basis</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => r.kind === 'group' ? (
                <tr key={i}>
                  <td colSpan={7} className="eyebrow" style={{
                    paddingTop: 16, color: 'var(--color-accent-700)', fontWeight: 600,
                  }}>{r.group}</td>
                </tr>
              ) : (
                <tr key={i} style={{ opacity: r.faded ? 0.55 : 1 }}>
                  <td className="num" style={{ ...R, color: 'var(--color-neutral-600)' }}>{r.sl}</td>
                  <td>{r.desc}</td>
                  <td className="num" style={{ ...R, color: 'var(--color-neutral-700)' }}>{r.std}</td>
                  <td className="num" style={{ ...R, fontWeight: 600 }}>{r.val}</td>
                  <td className="num" style={{ ...R, color: r.color }}>{r.variance}</td>
                  <td className="num" style={{ ...R, color: r.color }}>{r.variancePct}</td>
                  <td className="pretty" style={{ fontSize: 11, color: 'var(--color-neutral-600)', maxWidth: 280 }}>
                    {r.basis}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="pretty" style={{
          fontSize: 12, color: 'var(--color-neutral-700)', marginTop: 14,
          lineHeight: 1.55, maxWidth: '110ch',
        }}>{NOTE}</div>
      </Blueprint>
    </>
  )
}

/** The sheet's lines, in the MIS's order, from one month's summary. */
function sheetRows(data: MonthSummary): SheetRow[] {
  const t = data.totals
  const s = data.sheet
  const n = data.days
  const kwh = t.eb + t.solar
  const conv = s.conv_actual
  const cost = costPerKg(t.eb, t.solar, t.metered_kg, t.actual, n, t.output_share)
  const noPower = !cost?.metered
  const lint = lintPerKg()
  const cotton = cottonPerKg()
  const powerKg = cost?.power ?? null
  const otherKg = cost?.other ?? null
  // Power and overhead together; neither can stand in for the other, so
  // the line waits until both are known (an unmetered month is the one
  // case where "overhead only" is the honest figure).
  const convCost = otherKg === null ? null : (powerKg !== null ? powerKg + otherKg : noPower ? otherKg : null)
  const sale = s.sale.price
  const pctSale = (v: number | null) => (v === null || !sale ? null : (v / sale) * 100)
  const f = (v: number | null | undefined, d: number) =>
    v == null || !Number.isFinite(v) ? '–' : num(v, d)
  const sign = (v: number) => (v > 0 ? '+' : v < 0 ? '−' : '')

  const rows: SheetRow[] = []
  let sl = 0
  const group = (g: string) => rows.push({ kind: 'group', group: g })
  const row = (desc: string, std: number | null, val: number | null | undefined,
    d: number, basis: string) => {
    const has = val != null && Number.isFinite(val)
    const vr = has && std != null && Number.isFinite(std) ? (val as number) - std : null
    const good = vr === null ? null : vr >= 0
    rows.push({
      kind: 'row', sl: ++sl, desc, basis, faded: !has,
      std: std == null ? '' : f(std, d),
      val: f(val, d),
      variance: vr === null ? '' : sign(vr) + f(Math.abs(vr), d),
      variancePct: vr === null || !std ? '' : sign(vr) + f(Math.abs((vr / std) * 100), 2) + '%',
      color: good === null ? 'var(--color-neutral-700)' : good ? 'var(--c-bar1)' : 'var(--color-text)',
    })
  }

  group('Production')
  row('Total production in kgs', t.target, t.actual, 0, 'Ring frame kg against standard kg for frames that ran')
  row('40s conv production in kgs', s.conv_target, conv, 0, 'Each count × (count ÷ 40)^1.5')
  row('40s conv GPS', s.conv_gps_target, s.conv_gps_actual, 1, 'Converted grams per spindle per shift')
  row('Average count', null, s.avg_count, 2, 'Production-weighted Ne')
  row('Yarn realisation %', null, null, 1, 'Needs cotton issued to mixing — not in the reports')
  group('Utilisation')
  row('Overall utilisation %', null, t.utilization, 1, 'Machines running × time not lost to stoppages')
  row('Overall utilisation eff %', data.benchmark, t.util_x_eff, 1, 'Utilisation × efficiency, against the dashboard benchmark')
  group('Power')
  row('Units / kg', null, t.metered_kg ? kwh / t.metered_kg : null, 2,
    t.metered_days ? `EB + solar kWh ÷ kg, ${t.metered_days} of ${n} days metered` : 'No reliable meter readings this month')
  row('40s conv units / kg', null, s.conv_metered_kg ? kwh / s.conv_metered_kg : null, 2, 'kWh ÷ 40s converted kg')
  row('Power cost / kg', null, powerKg, 2, powerKg === null && cost?.metered ? 'Needs the EB tariff and solar cost per unit' : 'EB and solar ₹/unit from the cost inputs')
  row('Power cost / kg in %', null, pctSale(powerKg), 2, '% of sale value per kg')
  row('40s conv power cost / kg in Rs', null, powerKg !== null && conv ? (powerKg * t.actual) / conv : null, 2, 'Power cost over 40s converted kg')
  group('Maintenance')
  row('Maintenance cost / kg', null, null, 2, 'Needs stores & spares issue for maintenance')
  row('Maintenance cost / spdl in paisa', null, null, 2, 'Needs maintenance cost and installed spindles')
  row('40s conv maint cost / kg in Rs', null, null, 2, 'Needs maintenance cost')
  group('Wages & packing')
  row('Wage cost / kg', null, null, 2, 'Needs the monthly wage bill')
  row('40s conv wage cost / kg in Rs', null, null, 2, 'Needs the monthly wage bill')
  row('Packing cost / kg', null, null, 2, 'Needs packing material cost')
  group('Cost & realisation')
  row('Total production cost / kg', null, convCost, 2,
    convCost === null ? 'Needs the power tariffs and the monthly overhead'
      : (noPower ? 'Overhead only — power not metered' : 'Power + overhead') + ' · overhead from the cost inputs')
  row('Total production cost / kg in %', null, pctSale(convCost), 2, '% of sale value per kg')
  row('Total 40s conv production cost / kg in Rs', null, convCost !== null && conv ? (convCost * t.actual) / conv : null, 2, 'Conversion cost over 40s converted kg')
  row('Avg mixing cost / kg in %', null, pctSale(cotton), 2, cotton === null ? 'Needs the cotton rate and realization' : 'Cotton cost per kg of yarn ÷ sale value')
  row('Sale value (₹/kg)', null, sale, 2, sale ? `Ex-mill, ${s.sale.invoices} invoices this month` : 'No invoices this month')
  row('Sale value for 6 months avg', null, s.sale.price_6m, 2,
    s.sale.months_6m.length
      ? `Ex-mill, ${monthName(s.sale.months_6m[0])} – ${monthName(s.sale.months_6m[s.sale.months_6m.length - 1])}`
      : '')
  row('Cleaned cotton cost per kg', null, lint, 2, lint === null ? 'Needs the cotton rate per candy' : `Candy rate ÷ ${CANDY_KG} kg (cost inputs)`)
  row('Waste cotton value per kg', null, null, 2, 'Needs waste sale rates')
  return rows
}
